import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/feature/Footer';
import { finishingParts, popularColors, premiumColors } from '@/mocks/finishingTree';
import { useContainerParts } from '@/hooks/useContainerParts';
import type { ColorOption } from '@/mocks/finishingTree';
import {
  generateSummaryText,
  PROGRESS_STEP_LABELS,
  ALL_PROGRESS_STEPS,
} from '@/mocks/finishingPreviewJob';
import type { FinishingPreviewJob, FinishingPreviewResult } from '@/mocks/finishingPreviewJob';
import {
  createPreviewJob,
  pollJobStatus,
  getPreviewResult,
  getProductDetail,
} from '@/lib/finishingPreviewService';
import type { ProductDetailData } from '@/lib/finishingPreviewService';

// ─── Code safety helper ───
function extractCode(value: string, fallbackId: string): string {
  if (!value) return '';
  if (value.startsWith('part-')) return value.slice(5);
  if (value.startsWith('method-')) return value.slice(7);
  return value;
}

interface ConfirmedSelection {
  id: string;
  partId: string;
  partCode: string;
  partName: string;
  methodId: string;
  methodCode: string;
  methodName: string;
  colorHex: string;
  colorName: string;
}

let selectionCounter = 0;
function nextSelectionId(): string {
  selectionCounter += 1;
  return `sel-${selectionCounter}`;
}

export default function FinishingPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('products');

  const [product, setProduct] = useState<ProductDetailData | null>(null);
  const [productLoading, setProductLoading] = useState(true);

  const previousSelectedIds = (location.state as { selectedFinishingIds?: string[] } | null)?.selectedFinishingIds || [];
  const previousActiveIndex = (location.state as { activeImageIndex?: number } | null)?.activeImageIndex ?? 0;

  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [confirmedSelections, setConfirmedSelections] = useState<ConfirmedSelection[]>([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [lastPickedColor, setLastPickedColor] = useState<{ name: string; hex: string; pantoneCode?: string } | null>(null);
  const [colorTab, setColorTab] = useState<'popular' | 'premium' | 'custom'>('popular');
  const [customPantoneCode, setCustomPantoneCode] = useState('');
  const [customPantoneHex, setCustomPantoneHex] = useState('#000000');
  const [customColorError, setCustomColorError] = useState<string | null>(null);

  // ─── Inline image zoom ───
  const [zoomLevel, setZoomLevel] = useState(1);

  // ─── Supabase-backed Job Flow State ───
  const [summaryText, setSummaryText] = useState<string>('');
  const [additionalRequest, setAdditionalRequest] = useState<string>('');
  const [jobStatus, setJobStatus] = useState<FinishingPreviewJob | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<FinishingPreviewResult | null>(null);
  const [aiImageError, setAiImageError] = useState(false);
  const [elapsedDisplay, setElapsedDisplay] = useState<number>(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobStartTimeRef = useRef<number>(0);

  const safeIndex = product ? (previousActiveIndex < product.images.length ? previousActiveIndex : 0) : 0;
  const originalImageUrl = product ? (product.images[safeIndex] || '') : '';

  // ─── Fetch container parts from master_codes (filtered by product category) ───
  const { parts: containerParts, loading: partsLoading } = useContainerParts(
    product?.categoryL1 || '',
    product?.categoryL2 || '',
  );

  // Use DB parts when available, fall back to mock data
  const activeParts = containerParts.length > 0 ? containerParts : finishingParts;

  const selectedPart = useMemo(
    () => activeParts.find((p) => p.id === selectedPartId) || null,
    [selectedPartId, activeParts],
  );
  const selectedMethod = useMemo(
    () => selectedPart?.methods.find((m) => m.id === selectedMethodId) || null,
    [selectedPart, selectedMethodId],
  );

  // Reset job flow state when selections change
  useEffect(() => {
    setJobStatus(null);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setIsGenerating(false);
    setElapsedDisplay(0);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, [confirmedSelections]);

  // Auto-generate summary text when confirmed selections change
  useEffect(() => {
    if (confirmedSelections.length > 0) {
      const inputs = confirmedSelections.map((s) => ({
        partName: s.partName,
        methodName: s.methodName,
        colorName: s.colorName,
      }));
      setSummaryText(generateSummaryText(inputs));
    } else {
      setSummaryText('');
      setAdditionalRequest('');
    }
  }, [confirmedSelections]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  }, []);

  // Fetch actual product data from Supabase (falls back to mock)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setProductLoading(true);

    async function fetchProduct() {
      const data = await getProductDetail(id!);
      if (!cancelled) {
        setProduct(data);
        setProductLoading(false);
      }
    }

    fetchProduct();
    return () => { cancelled = true; };
  }, [id]);

  const handleSelectPart = (partId: string) => {
    if (selectedPartId === partId) {
      setSelectedPartId(null);
      setSelectedMethodId(null);
      setLastPickedColor(null);
    } else {
      setSelectedPartId(partId);
      setSelectedMethodId(null);
      setLastPickedColor(null);
    }
  };

  const handleSelectMethod = (methodId: string) => {
    if (methodId === selectedMethodId) {
      setSelectedMethodId(null);
      setLastPickedColor(null);
    } else {
      setSelectedMethodId(methodId);
      setLastPickedColor(null);
    }
  };

  const handleOpenColorPicker = () => {
    if (!selectedPart || !selectedMethod) return;
    setLastPickedColor(null);
  };

  const handleSelectColor = (color: ColorOption) => {
    if (!selectedPart || !selectedMethod) return;
    setShowColorPicker(false);
    setLastPickedColor({ name: color.name, hex: color.hex, pantoneCode: color.pantoneCode });
  };

  const handleConfirmCustomColor = () => {
    const trimmedCode = customPantoneCode.trim();
    if (!trimmedCode) {
      setCustomColorError(t('finishing.pantoneRequired'));
      return false;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(customPantoneHex)) {
      setCustomColorError(t('finishing.hexInvalid'));
      return false;
    }
    setCustomColorError(null);
    setLastPickedColor({
      name: t('finishing.customName'),
      hex: customPantoneHex,
      pantoneCode: trimmedCode,
    });
    return true;
  };

  const handleConfirmSelection = () => {
    if (!selectedPart || !selectedMethod || !lastPickedColor) return;
    const partCode = extractCode(selectedPart.code, selectedPart.id);
    const methodCode = extractCode(selectedMethod.code, selectedMethod.id);
    const newSelection: ConfirmedSelection = {
      id: nextSelectionId(),
      partId: selectedPart.id,
      partCode,
      partName: selectedPart.name,
      methodId: selectedMethod.id,
      methodCode,
      methodName: selectedMethod.name,
      colorHex: lastPickedColor.hex,
      colorName: lastPickedColor.pantoneCode || lastPickedColor.name,
    };
    setConfirmedSelections((prev) => [...prev, newSelection]);
    setSelectedPartId(null);
    setSelectedMethodId(null);
    setLastPickedColor(null);
  };

  const handleResetSelection = () => {
    setSelectedPartId(null);
    setSelectedMethodId(null);
    setLastPickedColor(null);
    setCustomPantoneCode('');
    setCustomPantoneHex('#000000');
    setCustomColorError(null);
    setColorTab('popular');
  };

  const confirmedPartIds = useMemo(
    () => new Set(confirmedSelections.map((s) => s.partId)),
    [confirmedSelections],
  );

  const handleRemoveSelection = (selId: string) => {
    setConfirmedSelections((prev) => prev.filter((s) => s.id !== selId));
  };

  // ─── Supabase-based Generation Flow ───
  // Readdy does NOT generate Kling prompts. It inserts structured JSON only.
  // The server worker reads structured_lines_json and builds the Kling prompt.
  const handleGeneratePreview = async () => {
    if (confirmedSelections.length === 0) return;

    // Build structured lines — batch ALL selections into one array (single Kling job)
    const structuredLines = confirmedSelections.map((s) => ({
      part_code: extractCode(s.partCode, s.partId),
      finish_code: extractCode(s.methodCode, s.methodId),
      color_code: s.colorHex,
    }));

    // Reset state
    setIsGenerating(true);
    setGenerationError(null);
    setPreviewResult(null);
    setAiImageError(false);
    setJobStatus(null);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }

    try {
      // Step 1: Insert pending job into Supabase (structured JSON only, no Kling prompt)
      const job = await createPreviewJob({
        product_id: product.id,
        working_image_id: product.workingImageId || originalImageUrl,
        structured_lines: structuredLines,
        additional_request: additionalRequest.trim() || undefined,
        source: 'readdy_customer_ui',
        request_locale: 'ko-KR',
      });

      setJobStatus(job);
      jobStartTimeRef.current = Date.now();
      setElapsedDisplay(0);

      // Start elapsed timer
      elapsedTimerRef.current = setInterval(() => {
        setElapsedDisplay(Math.floor((Date.now() - jobStartTimeRef.current) / 1000));
      }, 1000);

      // Step 2: Poll Supabase for job status every 2 seconds
      const jobId = job.id;
      pollTimerRef.current = setInterval(async () => {
        try {
          const status = await pollJobStatus(jobId);
          setJobStatus(status);

          if (status.status === 'completed') {
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (elapsedTimerRef.current) {
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }

            // Prefer API result_image_url (signed URL from Job API)
            if (status.result_image_url) {
              setPreviewResult({
                id: status.result_id || status.id,
                job_id: status.id,
                product_id: status.product_id,
                working_image_id: status.working_image_id,
                image_url: status.result_image_url,
                result_status: 'completed',
              });
              setIsGenerating(false);
            } else if (status.result_id) {
              // Fallback: fetch result from Supabase (legacy path)
              try {
                const result = await getPreviewResult(status.result_id);
                setPreviewResult(result);
                setIsGenerating(false);
              } catch (err) {
                setGenerationError(err instanceof Error ? err.message : 'Failed to fetch preview result.');
                setIsGenerating(false);
              }
            }
          }

          if (status.status === 'failed') {
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (elapsedTimerRef.current) {
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }
            setGenerationError(status.error_message || 'AI preview generation failed. Please try again.');
            setIsGenerating(false);
          }
        } catch (err) {
          console.error('[FinishingPreview] Poll iteration error:', err);
        }
      }, 2000);
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Failed to create preview job.');
      setIsGenerating(false);
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
    setJobStatus(null);
    setPreviewResult(null);
    handleGeneratePreview();
  };

  const handleClose = () => {
    navigate(`/products/${id}`, {
      state: {
        selectedFinishingIds: confirmedSelections.map((s) => s.id),
      },
    });
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  // Arrow connector state
  const connectorState = useMemo(() => {
    if (isGenerating) return 'processing';
    if (previewResult && !generationError) return 'done';
    return 'idle';
  }, [isGenerating, previewResult, generationError]);

  // ─── Tab label helpers ───
  const premiumSubLabels: Record<string, string> = {
    metallic: t('finishing.metallic'),
    pearl: t('finishing.pearl'),
    'soft-touch': t('finishing.softTouch'),
  };

  // ─── Render color tab content ───
  const renderColorTabContent = () => {
    // Popular Tab
    if (colorTab === 'popular') {
      return (
        <div className="max-h-[260px] overflow-y-auto pr-1 space-y-3">
          {Object.entries(popularByCategory).map(([category, colors]) => (
            <div key={category}>
              <h4 className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1.5 px-1">{category}</h4>
              <div className="grid grid-cols-2 gap-1">
                {colors.map((color) => (
                  <button
                    key={`pop-${color.pantoneCode}-${color.hex}`}
                    onClick={() => handleSelectColor(color)}
                    className="flex items-center gap-1.5 p-1.5 rounded-md hover:bg-cream-50 transition-colors cursor-pointer text-left border border-transparent hover:border-ink-100"
                  >
                    <div
                      className="w-7 h-7 rounded border border-ink-100 shrink-0"
                      style={{ backgroundColor: color.hex }}
                    />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-ink-700 truncate">{color.pantoneCode}</p>
                      <p className="text-[9px] text-ink-400 truncate">{color.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Premium Tab
    if (colorTab === 'premium') {
      return (
        <div className="max-h-[260px] overflow-y-auto pr-1 space-y-4">
          {Object.entries(premiumBySubcategory).map(([subKey, colors]) => (
            <div key={subKey}>
              <h4 className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1.5 px-1 flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${
                  subKey === 'metallic' ? 'bg-yellow-400' :
                  subKey === 'pearl' ? 'bg-pink-300' :
                  'bg-gray-400'
                }`} />
                {premiumSubLabels[subKey] || subKey}
              </h4>
              <div className="grid grid-cols-2 gap-1">
                {colors.map((color) => (
                  <button
                    key={`prem-${color.pantoneCode}-${color.hex}`}
                    onClick={() => handleSelectColor(color)}
                    className="flex items-center gap-1.5 p-1.5 rounded-md hover:bg-cream-50 transition-colors cursor-pointer text-left border border-transparent hover:border-ink-100"
                  >
                    <div
                      className="w-7 h-7 rounded border border-ink-100 shrink-0 relative overflow-hidden"
                      style={{
                        background: subKey === 'metallic'
                          ? `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 40%, ${color.hex} 70%, ${color.hex}cc 100%)`
                          : subKey === 'pearl'
                          ? `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}cc 30%, #ffffff88 50%, ${color.hex}cc 70%, ${color.hex} 100%)`
                          : color.hex,
                      }}
                    />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-ink-700 truncate">{color.pantoneCode}</p>
                      <p className="text-[9px] text-ink-400 truncate">{color.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Custom Tab
    return (
      <div className="flex flex-col gap-4 p-2">
        <div className="bg-cream-50/70 rounded-lg p-3 border border-ink-100">
          <p className="text-[10px] text-ink-400 mb-3">{t('finishing.customColorHint')}</p>

          {/* Pantone Code Input */}
          <div className="mb-3">
            <label className="text-[10px] font-bold text-ink-600 block mb-1">{t('finishing.pantoneCode')}</label>
            <input
              type="text"
              value={customPantoneCode}
              onChange={(e) => {
                setCustomPantoneCode(e.target.value);
                setCustomColorError(null);
              }}
              placeholder={t('finishing.pantonePlaceholder')}
              className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 transition-colors bg-white"
            />
          </div>

          {/* Hex Color Picker */}
          <div className="mb-3">
            <label className="text-[10px] font-bold text-ink-600 block mb-1">{t('finishing.hexCode')}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customPantoneHex}
                onChange={(e) => {
                  setCustomPantoneHex(e.target.value);
                  setCustomColorError(null);
                }}
                className="w-9 h-9 rounded-lg border border-ink-200 cursor-pointer shrink-0 p-0.5"
              />
              <input
                type="text"
                value={customPantoneHex}
                onChange={(e) => {
                  setCustomPantoneHex(e.target.value);
                  setCustomColorError(null);
                }}
                placeholder="#FF6B6B"
                className="flex-1 px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 transition-colors bg-white font-mono"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-ink-100">
            <div
              className="w-10 h-10 rounded-md border border-ink-200 shrink-0"
              style={{ backgroundColor: customPantoneHex }}
            />
            <div className="min-w-0">
              <p className="text-xs font-bold text-ink-700 truncate">{customPantoneCode || t('finishing.noPantone')}</p>
              <p className="text-[10px] text-ink-400 font-mono">{customPantoneHex}</p>
            </div>
          </div>

          {customColorError && (
            <p className="text-[10px] text-rose-500 mt-2 flex items-center gap-1">
              <i className="ri-error-warning-line text-xs" />
              {customColorError}
            </p>
          )}

          <button
            onClick={handleConfirmCustomColor}
            className="w-full mt-3 px-4 py-2 bg-warm-500 text-white text-xs font-bold rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
          >
            <i className="ri-check-line" />
            {t('finishing.selectThisColor')}
          </button>
        </div>
      </div>
    );
  };

  // ─── Render modal content ───
  const renderModalColorContent = () => {
    if (colorTab === 'popular') {
      return Object.entries(popularByCategory).map(([category, colors]) => (
        <div key={category} className="mb-5 last:mb-0">
          <h4 className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3">{category}</h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {colors.map((color) => (
              <button
                key={`modal-pop-${color.pantoneCode}-${color.hex}`}
                onClick={() => handleSelectColor(color)}
                className="flex flex-col items-start gap-1 p-2 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer group text-left"
              >
                <div
                  className="w-full h-10 rounded-md border border-ink-200 group-hover:border-ink-300 transition-colors shadow-sm"
                  style={{ backgroundColor: color.hex }}
                />
                <span className="text-[10px] font-bold text-ink-600 group-hover:text-ink-800 leading-tight">
                  {color.pantoneCode}
                </span>
                <span className="text-[9px] text-ink-400 leading-tight">
                  {color.name}
                </span>
                <span className="text-[9px] text-ink-300 font-mono">
                  {color.hex}
                </span>
              </button>
            ))}
          </div>
        </div>
      ));
    }

    if (colorTab === 'premium') {
      return Object.entries(premiumBySubcategory).map(([subKey, colors]) => (
        <div key={subKey} className="mb-5 last:mb-0">
          <h4 className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${
              subKey === 'metallic' ? 'bg-yellow-400' :
              subKey === 'pearl' ? 'bg-pink-300' :
              'bg-gray-400'
            }`} />
            {premiumSubLabels[subKey] || subKey}
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {colors.map((color) => (
              <button
                key={`modal-prem-${color.pantoneCode}-${color.hex}`}
                onClick={() => handleSelectColor(color)}
                className="flex flex-col items-start gap-1 p-2 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer group text-left"
              >
                <div
                  className="w-full h-10 rounded-md border border-ink-200 group-hover:border-ink-300 transition-colors shadow-sm"
                  style={{
                    background: subKey === 'metallic'
                      ? `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 40%, ${color.hex} 70%, ${color.hex}cc 100%)`
                      : subKey === 'pearl'
                      ? `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}cc 30%, #ffffff88 50%, ${color.hex}cc 70%, ${color.hex} 100%)`
                      : color.hex,
                  }}
                />
                <span className="text-[10px] font-bold text-ink-600 group-hover:text-ink-800 leading-tight">
                  {color.pantoneCode}
                </span>
                <span className="text-[9px] text-ink-400 leading-tight">
                  {color.name}
                </span>
                <span className="text-[9px] text-ink-300 font-mono">
                  {color.hex}
                </span>
              </button>
            ))}
          </div>
        </div>
      ));
    }

    // Custom tab in modal
    return (
      <div className="flex flex-col gap-4 p-2">
        <div className="bg-cream-50/70 rounded-lg p-4 border border-ink-100">
          <p className="text-xs text-ink-500 mb-3">{t('finishing.customColorHint')}</p>

          <div className="mb-4">
            <label className="text-xs font-bold text-ink-600 block mb-1.5">{t('finishing.pantoneCode')}</label>
            <input
              type="text"
              value={customPantoneCode}
              onChange={(e) => {
                setCustomPantoneCode(e.target.value);
                setCustomColorError(null);
              }}
              placeholder={t('finishing.pantonePlaceholder')}
              className="w-full px-3 py-2.5 text-sm border border-ink-200 rounded-lg focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 transition-colors bg-white"
            />
          </div>

          <div className="mb-4">
            <label className="text-xs font-bold text-ink-600 block mb-1.5">{t('finishing.hexCode')}</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={customPantoneHex}
                onChange={(e) => {
                  setCustomPantoneHex(e.target.value);
                  setCustomColorError(null);
                }}
                className="w-10 h-10 rounded-lg border border-ink-200 cursor-pointer shrink-0 p-0.5"
              />
              <input
                type="text"
                value={customPantoneHex}
                onChange={(e) => {
                  setCustomPantoneHex(e.target.value);
                  setCustomColorError(null);
                }}
                placeholder="#FF6B6B"
                className="flex-1 px-3 py-2.5 text-sm border border-ink-200 rounded-lg focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 transition-colors bg-white font-mono"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-ink-100">
            <div
              className="w-12 h-12 rounded-md border border-ink-200 shrink-0"
              style={{ backgroundColor: customPantoneHex }}
            />
            <div className="min-w-0">
              <p className="text-sm font-bold text-ink-700 truncate">{customPantoneCode || t('finishing.noPantone')}</p>
              <p className="text-xs text-ink-400 font-mono">{customPantoneHex}</p>
            </div>
          </div>

          {customColorError && (
            <p className="text-xs text-rose-500 mt-3 flex items-center gap-1">
              <i className="ri-error-warning-line" />
              {customColorError}
            </p>
          )}

          <button
            onClick={() => {
              if (handleConfirmCustomColor()) {
                setShowColorPicker(false);
              }
            }}
            className="w-full mt-4 px-4 py-2.5 bg-warm-500 text-white text-sm font-bold rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-check-line" />
            {t('finishing.selectThisColor')}
          </button>
        </div>
      </div>
    );
  };

  // Group popular colors by category
  const popularByCategory = useMemo(() => {
    const map: Record<string, ColorOption[]> = {};
    popularColors.forEach((c) => {
      if (!map[c.category]) map[c.category] = [];
      map[c.category].push(c);
    });
    return map;
  }, []);

  // Group premium colors by subcategory
  const premiumBySubcategory = useMemo(() => {
    const map: Record<string, ColorOption[]> = {};
    premiumColors.forEach((c) => {
      const key = c.subcategory || c.category;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return map;
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-ink-100">
        <div className="w-full px-4 md:px-6 lg:px-10 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-warm-100 flex items-center justify-center">
              <i className="ri-brush-line text-warm-500 text-sm" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-extrabold text-ink-700">{t('finishing.title')}</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="w-full">
        {/* Product Info Section */}
        {productLoading && (
          <section className="w-full bg-cream-50/30 border-b border-ink-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-20 flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-[3px] border-warm-200 border-t-warm-500 rounded-full animate-spin" />
                <p className="text-sm text-ink-400">{t('detail.loading')}</p>
              </div>
            </div>
          </section>
        )}

        {!productLoading && !product && (
          <section className="w-full bg-cream-50/30 border-b border-ink-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-20 flex items-center justify-center">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-rose-500 text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-ink-700">{t('detail.notFoundTitle')}</h3>
                <p className="text-sm text-ink-400">{t('detail.notFoundDesc')}</p>
                <button
                  onClick={() => navigate('/products')}
                  className="px-5 py-2.5 text-sm font-bold text-white bg-ink-700 rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('detail.backToList')}
                </button>
              </div>
            </div>
          </section>
        )}

        {product && (
        <>
        {/* Product Info Section */}
        <section className="w-full bg-cream-50/30 border-b border-ink-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-6 md:py-8">
            <div className="flex flex-col md:flex-row items-start gap-6 md:gap-8">
              {/* Left: Original Product Thumbnail */}
              <div className="w-full md:w-[176px] lg:w-[208px] shrink-0">
                <div className="aspect-square rounded-xl overflow-hidden bg-white border border-ink-100 flex items-center justify-center">
                  <img
                    src={originalImageUrl}
                    alt={`${product.name} ${t('finishing.original')}`}
                    className="w-full h-full object-contain p-2"
                  />
                </div>
              </div>

              {/* Right: Text Content */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-ink-400 mb-1">{t('products.card.sku', { sku: product.sku ?? '—' })}</p>
                <h2 className="text-xl md:text-2xl font-extrabold text-ink-700 mb-3">{product.name}</h2>
                <p className="text-sm text-ink-500 leading-relaxed mb-4">{product.description}</p>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="text-ink-400">{t('finishing.material')}</span>
                    <span className="text-ink-700 font-bold">{product.material}</span>
                  </div>
                  <div className="w-px h-4 bg-ink-200 hidden sm:block" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-ink-400">{t('finishing.capacity')}</span>
                    <span className="text-ink-700 font-bold">{product.capacity}</span>
                  </div>
                  <div className="w-px h-4 bg-ink-200 hidden sm:block" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-ink-400">{t('finishing.moq')}</span>
                    <span className="text-ink-700 font-bold">{product.moq.toLocaleString()} {product.moqUnit}</span>
                  </div>
                  <div className="w-px h-4 bg-ink-200 hidden sm:block" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-ink-400">{t('finishing.refPrice')}</span>
                    <span className="text-ink-700 font-bold">{t('finishing.quoteInquiry')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3-Step Tree Selector Section */}
        <section className="w-full">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
              <div>
                <h2 className="text-lg font-bold text-ink-700">{t('finishing.selectOptions')}</h2>
                <p className="text-sm text-ink-400">{t('finishing.selectOptionsDesc')}</p>
              </div>
              {confirmedSelections.length > 0 && (
                <button
                  onClick={() => {
                    setConfirmedSelections([]);
                    setSelectedPartId(null);
                    setSelectedMethodId(null);
                    setLastPickedColor(null);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1" />
                  {t('finishing.resetAll')}
                </button>
              )}
            </div>

            {/* Step Indicator */}
            <div className="flex items-center gap-0 mb-6">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  selectedPartId ? 'bg-warm-500 text-white' : 'bg-warm-100 text-warm-500'
                }`}>1</div>
                <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('finishing.selectPart')}</span>
              </div>
              <div className="flex-1 mx-3 h-px bg-ink-200" />
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  selectedMethodId ? 'bg-warm-500 text-white' : 'bg-warm-100 text-warm-500'
                }`}>2</div>
                <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('finishing.selectMethod')}</span>
              </div>
              <div className="flex-1 mx-3 h-px bg-ink-200" />
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  lastPickedColor ? 'bg-emerald-500 text-white' : 'bg-warm-100 text-warm-500'
                }`}>3</div>
                <span className="text-xs font-bold text-ink-600 whitespace-nowrap">{t('finishing.selectColor')}</span>
              </div>
            </div>

            {/* 3-Column Selector */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Column 1: Parts */}
              <div className="border border-ink-100 rounded-xl bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-ink-100 bg-cream-50/50">
                  <h3 className="text-sm font-bold text-ink-600 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-warm-100 text-warm-500 text-[10px] font-bold flex items-center justify-center">1</span>
                    {t('finishing.selectPart')}
                  </h3>
                </div>
                <div className="p-2 max-h-[320px] overflow-y-auto">
                  {activeParts.map((part) => {
                    const isActive = selectedPartId === part.id;
                    const isBlocked = confirmedPartIds.has(part.id);
                    if (isBlocked) {
                      return (
                        <div
                          key={part.id}
                          className="w-full text-left px-3 py-2 rounded-lg border-2 border-transparent bg-ink-50/50 opacity-60 cursor-not-allowed"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-ink-400">
                              <span className="text-[10px] font-mono text-ink-300 mr-1">[{part.code}]</span>
                              {part.engName} ({part.name})
                            </span>
                            <span className="text-xs text-emerald-500 flex items-center gap-1">
                              <i className="ri-check-line" />
                              {t('finishing.done')}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <button
                        key={part.id}
                        onClick={() => handleSelectPart(part.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer group ${
                          isActive
                            ? 'bg-warm-50 border-2 border-warm-400'
                            : 'border-2 border-transparent hover:bg-cream-50 hover:border-ink-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-bold transition-colors ${isActive ? 'text-warm-600' : 'text-ink-600 group-hover:text-ink-700'}`}>
                            <span className={`text-[10px] font-mono mr-1 ${isActive ? 'text-warm-400' : 'text-ink-300'}`}>[{part.code}]</span>
                            {part.engName} ({part.name})
                          </span>
                          <span className={`text-xs transition-opacity ${isActive ? 'text-warm-500 opacity-100' : 'text-ink-300 opacity-0 group-hover:opacity-100'}`}>
                            {t('finishing.methodsCount', { count: part.methods.length })}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Column 2: Methods */}
              <div className="border border-ink-100 rounded-xl bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-ink-100 bg-cream-50/50">
                  <h3 className="text-sm font-bold text-ink-600 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-warm-100 text-warm-500 text-[10px] font-bold flex items-center justify-center">2</span>
                    {t('finishing.selectMethod')}
                    {!selectedPart && <span className="text-ink-300 text-xs font-normal ml-1">{t('finishing.selectPartFirst')}</span>}
                  </h3>
                </div>
                <div className="p-2 max-h-[320px] overflow-y-auto">
                  {!selectedPart && (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <i className="ri-arrow-left-line text-2xl text-ink-200 mb-2" />
                      <p className="text-xs text-ink-300">{t('finishing.selectPartHint')}</p>
                    </div>
                  )}
                  {selectedPart && selectedPart.methods.map((method) => {
                    const isActive = selectedMethodId === method.id;
                    return (
                      <button
                        key={method.id}
                        onClick={() => handleSelectMethod(method.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${
                          isActive
                            ? 'bg-warm-50 border-2 border-warm-400'
                            : 'border-2 border-transparent hover:bg-cream-50 hover:border-ink-100'
                        }`}
                      >
                        <p className={`text-sm font-bold transition-colors ${isActive ? 'text-warm-600' : 'text-ink-600'}`}>
                          <span className={`text-[10px] font-mono mr-1 ${isActive ? 'text-warm-400' : 'text-ink-300'}`}>[{method.code}]</span>
                          {method.description} ({method.name})
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Column 3: Color */}
              <div className="border border-ink-100 rounded-xl bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-ink-100 bg-cream-50/50">
                  <h3 className="text-sm font-bold text-ink-600 flex items-center gap-1.5">
                    <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center transition-colors ${
                      lastPickedColor ? 'bg-emerald-500 text-white' : 'bg-warm-100 text-warm-500'
                    }`}>3</span>
                    {t('finishing.selectColor')}
                    {!selectedMethod && <span className="text-ink-300 text-xs font-normal ml-1">{t('finishing.selectMethodFirst')}</span>}
                    {selectedMethod && lastPickedColor && <span className="text-warm-500 text-xs font-normal ml-1">{t('finishing.confirmHint')}</span>}
                  </h3>
                </div>
                <div className="p-2 flex flex-col min-h-[200px]">
                  {!selectedMethod && (
                    <div className="flex flex-col items-center justify-center py-10 text-center flex-1">
                      <i className="ri-arrow-left-line text-2xl text-ink-200 mb-2" />
                      <p className="text-xs text-ink-300">{t('finishing.selectMethodHint')}</p>
                    </div>
                  )}
                  {selectedMethod && !lastPickedColor && (
                    <div className="w-full">
                      {/* Tab Bar */}
                      <div className="flex items-center bg-ink-50 rounded-lg p-0.5 mb-2">
                        <button
                          onClick={() => setColorTab('popular')}
                          className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                            colorTab === 'popular' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                          }`}
                        >
                          {t('finishing.popularColors')}
                        </button>
                        <button
                          onClick={() => setColorTab('premium')}
                          className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                            colorTab === 'premium' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                          }`}
                        >
                          {t('finishing.premium')}
                        </button>
                        <button
                          onClick={() => setColorTab('custom')}
                          className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                            colorTab === 'custom' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                          }`}
                        >
                          {t('finishing.custom')}
                        </button>
                      </div>

                      {renderColorTabContent()}
                    </div>
                  )}
                  {selectedMethod && lastPickedColor && (
                    <div className="w-full flex flex-col items-center gap-4 py-4">
                      <div className="text-center">
                        <p className="text-sm font-bold text-ink-600">{selectedPart?.name}</p>
                        <p className="text-xs text-ink-400 mt-0.5">{selectedMethod.name}</p>
                      </div>
                      {/* Selected Color Display */}
                      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3">
                        <div
                          className="w-12 h-12 rounded-lg border-2 border-white shadow-sm shrink-0"
                          style={{ backgroundColor: lastPickedColor.hex }}
                        />
                        <div className="text-left">
                          <p className="text-sm font-bold text-emerald-700">{lastPickedColor.pantoneCode || lastPickedColor.name}</p>
                          <p className="text-xs text-emerald-500 font-mono">{lastPickedColor.name}</p>
                          <p className="text-[10px] text-emerald-400 font-mono">{lastPickedColor.hex}</p>
                        </div>
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-emerald-500 text-xs" />
                        </div>
                      </div>
                      <button
                        onClick={() => { setLastPickedColor(null); setColorTab('popular'); }}
                        className="px-5 py-2.5 bg-white border-2 border-warm-400 text-warm-500 text-sm font-bold rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                      >
                        <i className="ri-palette-line" />
                        {t('finishing.changeColor')}
                      </button>
                      <p className="text-[10px] text-ink-300">{t('finishing.changeColorHint')}</p>
                    </div>
                  )}
                </div>
                {/* Confirm / Reset Buttons - only show when color is picked */}
                {selectedMethod && lastPickedColor && (
                  <div className="px-4 py-3 border-t border-ink-100 bg-cream-50/50 flex items-center justify-end gap-2">
                    <button
                      onClick={handleResetSelection}
                      className="px-3.5 py-2 text-xs font-bold text-ink-500 border border-ink-200 rounded-lg hover:bg-white hover:border-ink-300 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-refresh-line text-sm" />
                      {t('finishing.reselect')}
                    </button>
                    <button
                      onClick={handleConfirmSelection}
                      className="px-3.5 py-2 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
                    >
                      <i className="ri-check-line text-sm" />
                      {t('finishing.confirm')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Confirmed Selections */}
        {confirmedSelections.length > 0 && (
          <section className="w-full bg-cream-50/40 border-t border-ink-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-warm-100 flex items-center justify-center">
                    <i className="ri-check-double-line text-warm-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-ink-700">
                      {t('finishing.selectedCount', { count: confirmedSelections.length })}
                    </h3>
                    <p className="text-xs text-ink-400">{t('finishing.optionsIncluded')}</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {confirmedSelections.map((sel) => (
                  <span
                    key={sel.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border bg-white border-ink-200 text-ink-600"
                  >
                    <span
                      className="w-3.5 h-3.5 rounded-full border border-ink-200 shrink-0"
                      style={{ backgroundColor: sel.colorHex }}
                    />
                    <span className="font-bold">{sel.partName.split('(')[0].trim()}</span>
                    <span className="text-ink-400">·</span>
                    <span>{sel.methodName.split('(')[0].trim()}</span>
                    <span className="text-ink-300">· {sel.colorName}</span>
                    <button
                      onClick={() => handleRemoveSelection(sel.id)}
                      className="w-4 h-4 rounded-full bg-ink-100 flex items-center justify-center hover:bg-ink-200 transition-colors cursor-pointer ml-0.5"
                    >
                      <i className="ri-close-line text-[10px] text-ink-400" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Two-Image Comparison */}
        <section className="relative w-full bg-cream-50/50 border-b border-ink-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
            {/* AI Preview Button */}
            <div className="flex justify-center mb-6">
              <button
                onClick={handleGeneratePreview}
                disabled={confirmedSelections.length === 0 || isGenerating}
                className={`w-[200px] py-3 rounded-lg text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  confirmedSelections.length === 0 || isGenerating
                    ? 'bg-ink-200 text-ink-400 cursor-not-allowed'
                    : 'bg-warm-500 text-white hover:bg-warm-600 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)]'
                }`}
              >
                {isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('finishing.generating')}
                  </>
                ) : (
                  <>
                    <i className="ri-sparkling-2-line" />
                    {t('finishing.aiPreviewButton')}
                  </>
                )}
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-4 sm:gap-5">
              {/* Original Image Card */}
              <div className="flex-1 bg-white rounded-xl border border-ink-100 p-4 md:p-5 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md bg-ink-100 flex items-center justify-center">
                    <i className="ri-image-line text-ink-500 text-xs" />
                  </div>
                  <span className="text-xs font-bold text-ink-500 uppercase tracking-wider">{t('finishing.original')}</span>
                </div>
                <div className="flex-1 aspect-square rounded-lg overflow-hidden bg-cream-50 border border-ink-100 flex items-center justify-center p-4">
                  <img
                    src={originalImageUrl}
                    alt={`${product.name} ${t('finishing.original')}`}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* AI Arrow Connector */}
              <div className="hidden sm:flex items-center justify-center shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  connectorState === 'done' ? 'bg-emerald-100' : 'bg-warm-100'
                }`}>
                  {connectorState === 'processing' && (
                    <div className="w-4 h-4 border-2 border-warm-300 border-t-warm-500 rounded-full animate-spin" />
                  )}
                  {connectorState === 'done' && (
                    <i className="ri-check-line text-emerald-500 text-lg" />
                  )}
                  {connectorState === 'idle' && (
                    <i className="ri-arrow-right-line text-warm-500 text-lg" />
                  )}
                </div>
              </div>

              {/* Processed Image Card */}
              <div className="flex-1 bg-white rounded-xl border border-ink-100 p-4 md:p-5 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md bg-warm-100 flex items-center justify-center">
                    <i className="ri-sparkling-line text-warm-500 text-xs" />
                  </div>
                  <span className="text-xs font-bold text-warm-600 uppercase tracking-wider">{t('finishing.aiFinished')}</span>
                </div>
                <div
                  className={`flex-1 aspect-square rounded-lg overflow-hidden bg-cream-50 border border-ink-100 flex items-center justify-center relative group ${
                    !isGenerating && previewResult && !generationError && !aiImageError
                      ? zoomLevel > 1 ? 'cursor-zoom-out' : 'cursor-zoom-in'
                      : ''
                  }`}
                  onClick={() => {
                    if (!isGenerating && previewResult && !generationError && !aiImageError) {
                      handleZoomIn();
                    }
                  }}
                  onContextMenu={(e) => {
                    if (!isGenerating && previewResult && !generationError && !aiImageError) {
                      handleZoomOut(e);
                    }
                  }}
                  onDoubleClick={() => {
                    if (!isGenerating && previewResult && !generationError && !aiImageError) {
                      handleZoomReset();
                    }
                  }}
                >
                  {isGenerating && (
                    <div className="flex flex-col items-center gap-3 animate-[fadeIn_0.3s_ease-out]">
                      <div className="w-10 h-10 border-[3px] border-warm-200 border-t-warm-500 rounded-full animate-spin" />
                      <div className="text-center">
                        <p className="text-sm font-bold text-ink-600">{t('finishing.aiGenerating')}</p>
                        <p className="text-[10px] text-ink-400 mt-0.5">
                          {confirmedSelections.map((s) => s.methodName.split('(')[0].trim()).join(', ')}
                        </p>
                      </div>
                    </div>
                  )}

                  {!isGenerating && previewResult && !generationError && (
                    <>
                      <div className="w-full h-full p-4 animate-[fadeIn_0.5s_ease-out]">
                        <img
                          src={aiImageError ? originalImageUrl : previewResult.image_url}
                          alt={`${product.name} ${t('finishing.aiFinished')}`}
                          onError={() => setAiImageError(true)}
                          className="w-full h-full object-contain transition-transform duration-300 ease-out"
                          style={{ transform: `scale(${zoomLevel})` }}
                          draggable={false}
                        />
                      </div>
                      {/* Zoom indicator */}
                      {!aiImageError && zoomLevel > 1 && (
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white text-[10px] font-mono font-bold z-10">
                          {zoomLevel.toFixed(1)}x
                        </div>
                      )}
                      {!aiImageError && zoomLevel > 1 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleZoomReset(); }}
                          className="absolute bottom-3 right-3 w-7 h-7 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center cursor-pointer transition-all duration-200 z-10 backdrop-blur-sm"
                          title={t('detail.reset')}
                        >
                          <i className="ri-aspect-ratio-line text-xs" />
                        </button>
                      )}
                      {aiImageError && (
                        <div className="absolute inset-x-0 bottom-0 bg-warm-50/95 border-t border-warm-200 p-3 flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <i className="ri-information-line text-warm-500 text-sm" />
                            <span className="text-xs font-bold text-warm-600">{t('finishing.aiFailed')}</span>
                          </div>
                          <p className="text-[10px] text-ink-500">{t('finishing.aiFailedHint')}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {confirmedSelections.map((s) => (
                              <span key={s.id} className="text-[9px] px-1.5 py-0.5 rounded bg-white border border-ink-200 text-ink-600">
                                <span className="inline-block w-2 h-2 rounded-full border border-ink-200 mr-1" style={{ backgroundColor: s.colorHex }} />
                                {s.partName.split('(')[0].trim()} · {s.methodName.split('(')[0].trim()} · {s.colorName}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!isGenerating && generationError && (
                    <div className="flex flex-col items-center gap-2 text-center px-4">
                      <i className="ri-error-warning-line text-3xl text-rose-400" />
                      <p className="text-sm text-rose-500">{generationError}</p>
                      <button
                        onClick={handleRetryGeneration}
                        className="text-xs text-warm-500 hover:text-warm-600 underline cursor-pointer"
                      >
                        {t('finishing.retry')}
                      </button>
                    </div>
                  )}

                  {!isGenerating && !previewResult && !generationError && (
                    <div className="flex flex-col items-center gap-2 text-center px-4">
                      <i className="ri-sparkling-2-line text-3xl text-ink-200" />
                      <p className="text-sm text-ink-400 font-medium">{t('finishing.aiImage')}</p>
                      <p className="text-[10px] text-ink-300 max-w-[180px]">
                        {t('finishing.aiImageHint')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Summary Section (structured data preview, NOT Kling prompt) ─── */}
        {confirmedSelections.length > 0 && summaryText && (
          <section className="w-full border-t border-ink-100 bg-white">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-warm-100 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-warm-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-ink-700">{t('finishing.summary')}</h3>
                  <p className="text-xs text-ink-400">{t('finishing.summaryDesc')}</p>
                </div>
              </div>

              {/* Summary display (for user confirmation only) */}
              <div className="mb-4 p-4 rounded-lg border border-ink-200 bg-cream-50/40">
                <label className="text-xs font-bold text-ink-500 uppercase tracking-wider mb-2 block">
                  <i className="ri-eye-line text-warm-500 mr-1" />
                  {t('finishing.selectionSummary')}
                </label>
                <p className="text-sm text-ink-700 leading-relaxed">{summaryText}</p>
              </div>

              {/* Additional request input */}
              <div className="mb-6">
                <label className="text-xs font-bold text-ink-500 uppercase tracking-wider mb-2 block">
                  <i className="ri-edit-line text-warm-500 mr-1" />
                  {t('finishing.additionalRequest')}
                </label>
                <textarea
                  value={additionalRequest}
                  onChange={(e) => setAdditionalRequest(e.target.value)}
                  placeholder={t('finishing.additionalRequestPlaceholder')}
                  rows={3}
                  disabled={isGenerating}
                  className="w-full px-4 py-3 text-sm text-ink-700 bg-white border border-ink-200 rounded-lg resize-none focus:outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-200 transition-colors leading-relaxed placeholder:text-ink-300 disabled:opacity-50"
                />
                <p className="text-[10px] text-ink-300 mt-1">
                  {t('finishing.additionalRequestHint')}
                </p>
              </div>

              {/* Generate Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleGeneratePreview}
                  disabled={isGenerating}
                  className={`px-8 py-3.5 rounded-lg text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    isGenerating
                      ? 'bg-ink-200 text-ink-400 cursor-not-allowed'
                      : 'bg-warm-500 text-white hover:bg-warm-600 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)]'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('finishing.generating')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      {t('finishing.generatePreview')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ─── NEW: Progress Panel ─── */}
        {isGenerating && jobStatus && (
          <section className="w-full border-t border-ink-100 bg-cream-50/30">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-6 md:py-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-warm-100 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-warm-300 border-t-warm-500 rounded-full animate-spin" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-ink-700">{t('finishing.progressTitle')}</h3>
                  <p className="text-xs text-ink-400">Job ID: {jobStatus.id}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-ink-500 uppercase tracking-wider">
                    {jobStatus.progress_step.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs font-bold text-warm-600">{jobStatus.progress_percent}%</span>
                </div>
                <div className="w-full h-2.5 bg-ink-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warm-500 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${jobStatus.progress_percent}%` }}
                  />
                </div>
              </div>

              {/* Status Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg border border-ink-100 p-3">
                  <p className="text-[10px] text-ink-400 uppercase tracking-wider mb-1">{t('finishing.currentStatus')}</p>
                  <p className="text-sm font-bold text-ink-700 capitalize">
                    {jobStatus.progress_step.replace(/_/g, ' ')}
                  </p>
                </div>
                <div className="bg-white rounded-lg border border-ink-100 p-3">
                  <p className="text-[10px] text-ink-400 uppercase tracking-wider mb-1">{t('finishing.elapsed')}</p>
                  <p className="text-sm font-bold text-ink-700">
                    {elapsedDisplay}s
                  </p>
                </div>
                <div className="bg-white rounded-lg border border-ink-100 p-3">
                  <p className="text-[10px] text-ink-400 uppercase tracking-wider mb-1">{t('finishing.message')}</p>
                  <p className="text-sm font-medium text-ink-600">{jobStatus.progress_message}</p>
                </div>
              </div>

              {/* Progress Steps Timeline */}
              <div className="mt-5">
                <div className="flex items-center gap-1 overflow-x-auto pb-2">
                  {ALL_PROGRESS_STEPS.map((step, idx) => {
                    const stepIndex = ALL_PROGRESS_STEPS.indexOf(jobStatus.progress_step);
                    const isPast = idx < stepIndex;
                    const isCurrent = idx === stepIndex;
                    const stepLabel = PROGRESS_STEP_LABELS[step] || step.replace(/_/g, ' ');
                    return (
                      <div key={step} className="flex items-center gap-1 shrink-0">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium whitespace-nowrap ${
                          isPast ? 'bg-emerald-100 text-emerald-600' :
                          isCurrent ? 'bg-warm-100 text-warm-600' :
                          'bg-ink-50 text-ink-300'
                        }`}>
                          <span className={`w-3 h-3 rounded-full flex items-center justify-center text-[7px] ${
                            isPast ? 'bg-emerald-500 text-white' :
                            isCurrent ? 'bg-warm-500 text-white' :
                            'bg-ink-200 text-ink-400'
                          }`}>
                            {isPast ? '✓' : idx + 1}
                          </span>
                          {stepLabel}
                        </div>
                        {idx < ALL_PROGRESS_STEPS.length - 1 && (
                          <div className={`w-3 h-px ${idx < stepIndex ? 'bg-emerald-300' : 'bg-ink-200'}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ─── NEW: Result Section ─── */}
        {previewResult && !isGenerating && (
          <section className="w-full border-t border-ink-100 bg-emerald-50/20">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <i className="ri-check-double-line text-emerald-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-ink-700">{t('finishing.complete')}</h3>
                  <p className="text-xs text-ink-400">
                    Result ID: {previewResult.id} · {previewResult.width}x{previewResult.height}
                  </p>
                </div>
              </div>

              {/* Before / After mini comparison */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-ink-100 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-md bg-ink-100 flex items-center justify-center">
                      <i className="ri-image-line text-ink-500 text-xs" />
                    </div>
                    <span className="text-xs font-bold text-ink-500 uppercase tracking-wider">{t('finishing.original')}</span>
                  </div>
                  <div className="aspect-square rounded-lg overflow-hidden bg-cream-50 border border-ink-100 p-3">
                    <img src={originalImageUrl} alt={t('finishing.original')} className="w-full h-full object-contain" />
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-warm-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-md bg-warm-100 flex items-center justify-center">
                      <i className="ri-sparkling-line text-warm-500 text-xs" />
                    </div>
                    <span className="text-xs font-bold text-warm-600 uppercase tracking-wider">{t('finishing.aiPreview')}</span>
                  </div>
                  <div
                    className={`aspect-square rounded-lg overflow-hidden bg-cream-50 border border-warm-200 relative group p-3 ${
                      !aiImageError ? zoomLevel > 1 ? 'cursor-zoom-out' : 'cursor-zoom-in' : ''
                    }`}
                    onClick={() => {
                      if (!aiImageError) handleZoomIn();
                    }}
                    onContextMenu={(e) => {
                      if (!aiImageError) handleZoomOut(e);
                    }}
                    onDoubleClick={() => {
                      if (!aiImageError) handleZoomReset();
                    }}
                  >
                    <img
                      src={aiImageError ? originalImageUrl : previewResult.image_url}
                      alt={t('finishing.aiPreview')}
                      onError={() => setAiImageError(true)}
                      className="w-full h-full object-contain transition-transform duration-300 ease-out"
                      style={{ transform: `scale(${zoomLevel})` }}
                      draggable={false}
                    />
                    {/* Zoom indicator */}
                    {!aiImageError && zoomLevel > 1 && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white text-[10px] font-mono font-bold z-10">
                        {zoomLevel.toFixed(1)}x
                      </div>
                    )}
                    {!aiImageError && zoomLevel > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleZoomReset(); }}
                        className="absolute bottom-3 right-3 w-7 h-7 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center cursor-pointer transition-all duration-200 z-10 backdrop-blur-sm"
                        title={t('detail.reset')}
                      >
                        <i className="ri-aspect-ratio-line text-xs" />
                      </button>
                    )}
                    {aiImageError && (
                      <div className="absolute inset-x-0 bottom-0 bg-warm-50/95 border-t border-warm-200 p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className="ri-information-line text-warm-500 text-sm" />
                          <span className="text-xs font-bold text-warm-600">{t('finishing.aiFailed')}</span>
                        </div>
                        <p className="text-[10px] text-ink-500">{t('finishing.aiFailedHint')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={handleRetryGeneration}
                  className="px-5 py-2.5 text-sm font-bold text-warm-600 border-2 border-warm-300 rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-refresh-line" />
                  {t('finishing.generateAgain')}
                </button>
                <button
                  onClick={handleClose}
                  className="px-5 py-2.5 text-sm font-bold text-white bg-ink-700 rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-check-line" />
                  {t('finishing.useThisPreview')}
                </button>
                <button
                  onClick={() => navigate(`/products/${id}`)}
                  className="px-5 py-2.5 text-sm font-bold text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  {t('finishing.viewEstimate')}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ─── NEW: Error Section ─── */}
        {generationError && !isGenerating && !previewResult && (
          <section className="w-full border-t border-ink-100 bg-rose-50/20">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mb-4">
                  <i className="ri-error-warning-line text-rose-500 text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-ink-700 mb-2">{t('finishing.failed')}</h3>
                <p className="text-sm text-rose-600 mb-6 max-w-lg">{generationError}</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleRetryGeneration}
                    className="px-6 py-2.5 text-sm font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-refresh-line" />
                    {t('finishing.retryBtn')}
                  </button>
                  <button
                    onClick={() => {
                      setGenerationError(null);
                      setJobStatus(null);
                      setPreviewResult(null);
                      setAiImageError(false);
                    }}
                    className="px-6 py-2.5 text-sm font-bold text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-edit-line" />
                    {t('finishing.editOptions')}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="w-full border-t border-ink-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-5 flex items-center justify-center gap-3">
            <button
              onClick={() => {
                setConfirmedSelections([]);
                setSelectedPartId(null);
                setSelectedMethodId(null);
                setLastPickedColor(null);
                navigate(`/products/${id}`);
              }}
              className="px-4 py-2.5 text-sm font-medium text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('finishing.cancelAndBack')}
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-ink-700 text-white text-sm font-bold rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-check-line" />
              {confirmedSelections.length > 0
                ? t('finishing.applySave', { count: confirmedSelections.length })
                : t('finishing.saveUse')}
            </button>
          </div>
        </section>
        </>
        )}
      </main>

      <Footer />

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setShowColorPicker(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-xl border border-ink-100 w-full max-w-2xl max-h-[85vh] overflow-hidden mx-4 animate-[fadeIn_0.2s_ease-out]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
              <div>
                <h3 className="text-base font-bold text-ink-700">{t('finishing.selectColor')}</h3>
                <p className="text-xs text-ink-400 mt-0.5">
                  {selectedPart?.name} · {selectedMethod?.name}
                </p>
              </div>
              <button
                onClick={() => setShowColorPicker(false)}
                className="w-8 h-8 rounded-lg hover:bg-ink-50 flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-ink-400 text-lg" />
              </button>
            </div>

            {/* Tab Bar */}
            <div className="px-6 pt-4 pb-0">
              <div className="flex items-center bg-ink-50 rounded-lg p-0.5">
                <button
                  onClick={() => setColorTab('popular')}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                    colorTab === 'popular' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                  }`}
                >
                  {t('finishing.popularColors')}
                </button>
                <button
                  onClick={() => setColorTab('premium')}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                    colorTab === 'premium' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                  }`}
                >
                  {t('finishing.premium')}
                </button>
                <button
                  onClick={() => setColorTab('custom')}
                  className={`flex-1 py-2 text-xs font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                    colorTab === 'custom' ? 'bg-white text-ink-700 shadow-sm' : 'text-ink-400 hover:text-ink-500'
                  }`}
                >
                  {t('finishing.custom')}
                </button>
              </div>
            </div>

            {/* Color Grid */}
            <div className="px-6 py-5 overflow-y-auto max-h-[60vh]">
              {renderModalColorContent()}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-ink-100 bg-cream-50/50 flex items-center justify-between">
              <span className="text-[10px] text-ink-400">
                {colorTab === 'popular' ? `${t('finishing.popularColors')} (${popularColors.length})` : colorTab === 'premium' ? `${t('finishing.premium')} (${premiumColors.length})` : t('finishing.custom')}
              </span>
              <button
                onClick={() => setShowColorPicker(false)}
                className="px-4 py-1.5 text-xs font-medium text-ink-500 border border-ink-200 rounded-lg hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('finishing.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}